import { AppearanceError } from './errors.js';

/*
 * The first-open welcome (CLAUDE.md §4 appearance, Saif 21 Oct 2026): after the glide intro, a tagline holds for a beat
 * under the logo, then a few swipe cards say what the app does, with Join and Browse first. Shown once per install (and
 * once per browser), never again after Skip, Browse or Join. Everything about it is data Saif edits in the admin: the
 * switch, the tagline and the cards (photo, title, one line) in display order. Stored as site_settings row `app_welcome`,
 * served in GET /api/appearance as `welcome` with each card's photo as a URL.
 */

export const WELCOME_MAX_CARDS = 5;
export const WELCOME_TAGLINE_MAX = 40;
export const WELCOME_TITLE_MAX = 32;
export const WELCOME_TEXT_MAX = 100;
const IMAGE_MAX = 300;

export interface WelcomeCard {
  id: string;
  /** A photo: an R2 key from the What's new upload ("home/…"), a path of the app's own ("/placeholder-rice.webp"), or null. */
  image: string | null;
  title: string;
  text: string;
}
export interface WelcomeSetting { enabled: boolean; tagline: string; cards: WelcomeCard[] }

/** What the app gets: the same, with each photo as a URL (relative to the API for an upload, to the app for its own). */
export interface WelcomeView { enabled: boolean; tagline: string; cards: { id: string; imageUrl: string | null; title: string; text: string }[] }

export const DEFAULT_WELCOME: WelcomeSetting = {
  enabled: true,
  tagline: 'One dish, done well',
  cards: [
    { id: 'flavours', image: '/placeholder-rice.webp', title: 'Five tikka flavours', text: 'Chicken or paneer, in a bowl, a wrap or on chips. Mix two flavours in a bowl.' },
    { id: 'rewards', image: '/placeholder-just.webp', title: 'Earn with every order', text: '5 points per £1, free food from 250 points. Your card works at the till too.' },
    { id: 'ahead', image: '/placeholder-wrap.webp', title: 'Order ahead, skip the queue', text: 'Pick a collection time and watch your order live. It’s ready when you are.' },
  ],
};

const clean = (v: unknown, max: number): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');
const isImage = (v: unknown): v is string => typeof v === 'string' && v.length <= IMAGE_MAX && !v.includes('..') && (v.startsWith('home/') || v.startsWith('/'));

/** The setting to save, checked: Saif's reasons come back as `bad_welcome`. */
export function checkWelcome(raw: unknown): WelcomeSetting {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  if (typeof o.enabled !== 'boolean') throw new AppearanceError('Say whether the welcome is on or off', 'bad_welcome');
  const tagline = typeof o.tagline === 'string' ? o.tagline.replace(/\s+/g, ' ').trim() : '';
  if (tagline.length > WELCOME_TAGLINE_MAX) throw new AppearanceError(`The tagline is at most ${WELCOME_TAGLINE_MAX} characters`, 'bad_welcome');
  const rawCards = Array.isArray(o.cards) ? o.cards : [];
  if (rawCards.length > WELCOME_MAX_CARDS) throw new AppearanceError(`At most ${WELCOME_MAX_CARDS} cards`, 'bad_welcome');
  if (o.enabled && rawCards.length === 0) throw new AppearanceError('Add at least one card, or switch the welcome off', 'bad_welcome');
  const ids = new Set<string>();
  const cards = rawCards.map((c, i) => {
    const r = c && typeof c === 'object' ? (c as Record<string, unknown>) : {};
    const id = typeof r.id === 'string' && /^[a-z0-9_-]{1,40}$/i.test(r.id) ? r.id : `card_${i + 1}`;
    if (ids.has(id)) throw new AppearanceError('Two cards have the same id', 'bad_welcome');
    ids.add(id);
    const title = typeof r.title === 'string' ? r.title.replace(/\s+/g, ' ').trim() : '';
    const text = typeof r.text === 'string' ? r.text.replace(/\s+/g, ' ').trim() : '';
    if (!title) throw new AppearanceError(`Card ${i + 1} needs a title`, 'bad_welcome');
    if (title.length > WELCOME_TITLE_MAX) throw new AppearanceError(`Card ${i + 1}’s title is at most ${WELCOME_TITLE_MAX} characters`, 'bad_welcome');
    if (text.length > WELCOME_TEXT_MAX) throw new AppearanceError(`Card ${i + 1}’s line is at most ${WELCOME_TEXT_MAX} characters`, 'bad_welcome');
    if (r.image != null && !isImage(r.image)) throw new AppearanceError(`Card ${i + 1}’s photo isn’t one of ours`, 'bad_welcome');
    return { id, image: r.image == null ? null : r.image, title, text };
  });
  return { enabled: o.enabled, tagline, cards };
}

/** The stored setting, read leniently: anything unreadable takes the starting value. */
export function storedWelcome(raw: unknown): WelcomeSetting {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!o || typeof o.enabled !== 'boolean' || !Array.isArray(o.cards)) return DEFAULT_WELCOME;
  const cards = o.cards.slice(0, WELCOME_MAX_CARDS).flatMap((c, i): WelcomeCard[] => {
    const r = c && typeof c === 'object' ? (c as Record<string, unknown>) : {};
    const title = clean(r.title, WELCOME_TITLE_MAX);
    if (!title) return [];
    return [{ id: typeof r.id === 'string' && r.id ? r.id.slice(0, 40) : `card_${i + 1}`, image: isImage(r.image) ? r.image : null, title, text: clean(r.text, WELCOME_TEXT_MAX) }];
  });
  return { enabled: o.enabled && cards.length > 0, tagline: clean(o.tagline, WELCOME_TAGLINE_MAX), cards };
}

/** The setting as the app sees it: each photo as a URL (`photoUrl` turns an R2 key into the API's photo address). */
export function welcomeView(w: WelcomeSetting, photoUrl: (key: string) => string): WelcomeView {
  return { enabled: w.enabled, tagline: w.tagline, cards: w.cards.map((c) => ({ id: c.id, imageUrl: c.image == null ? null : c.image.startsWith('/') ? c.image : photoUrl(c.image), title: c.title, text: c.text })) };
}
